import { KERB_LANE } from '@accra/shared';
import type { BusState } from '../../game/RunState';
import type { DrivingAssist } from '../driving';
import { isSettledIn } from '../lanes';

const ASSIST = {
  /** Assist starts this far before the box centre. */
  range: 40,
  /** Only helps if the driver has already slowed below this (m/s). */
  engageBelow: 11,
  /** Gentle deceleration used to glide into the box. */
  decel: 5,
} as const;

const OFF: DrivingAssist = { maxSpeed: Number.POSITIVE_INFINITY };
const HOLD: DrivingAssist = { maxSpeed: 0 };

/** approach: not stopped yet · holding: stopped, people still boarding · done: ready to go. */
export type StopPhase = 'approach' | 'holding' | 'done';

/**
 * Stop assist: once you're in the kerb lane and slowing down near a stop,
 * it caps your speed so the trotro glides to a halt at the centre of the box.
 * While people are boarding it holds the bus; gas always overrides.
 * Fixes the "overshot the tiny box" frustration from the benchmark game.
 */
export function stopAssist(bus: BusState, point: { position: number }, gas: boolean, phase: StopPhase): DrivingAssist {
  if (gas || phase === 'done') return OFF;
  if (phase === 'holding') return HOLD;
  if (!isSettledIn(bus.x, KERB_LANE, 0.9)) return OFF;
  const toCentre = point.position - bus.s;
  if (toCentre < -1 || toCentre > ASSIST.range) return OFF;
  if (bus.speed > ASSIST.engageBelow) return OFF;
  const glide = Math.sqrt(2 * ASSIST.decel * Math.max(0, toCentre));
  // Never let it speed back up past the engage limit, or the assist would switch itself off.
  return { maxSpeed: Math.min(glide, ASSIST.engageBelow) };
}
